// toolGuardrail.js — E3-WS4-T01 (code-tab-to-9).
// @arch:pure-engine
//
// File-Deletion and External-Write guardrail over the evaluateToolPolicy seam.
// When the flag is ON, blocks:
//   1. Auto-`rm` / delete operations (run_command with rm/del/shred)
//   2. write_file / apply_edit to paths outside the resolved project root
//
// Flag `apolo_tool_guardrail` default OFF. When OFF, returns { denied: false }
// for everything — behavior byte-identical to today.
//
// PURE (Tier 1): no react/window/IPC. Uses path.resolve/path.relative logic
// inline (no node:path import — this runs in the browser too).

// E3-WS3-T04 — the path-arg alias set comes from the UNIFIED tool-descriptor registry
// (single source), so this guardrail and the executor can never disagree on what counts as a
// tool's path argument. The registry is a pure leaf in loop/ (no executor graph), so importing
// it keeps this guardrail framework-free.
import { resolvePathArg } from './toolDescriptors.js';

const DELETE_PATTERNS = /\b(rm|rmdir|del|shred|unlink)\b/i;

function normalizePath(p) {
  return String(p ?? '').replace(/\\/g, '/').replace(/\/+/g, '/');
}

function isInsideRoot(root, target) {
  const t = normalizePath(target);
  // A3 (code-tab-to-10): a ROOT-RELATIVE path (the model's dominant write form, e.g.
  // "src/App.jsx") is re-rooted by the executor → inside by construction. Only "../"
  // traversal escapes. Absolute paths fall through to the literal containment check.
  if (!t.startsWith('/')) return !(t === '..' || t.startsWith('../') || t.includes('/../'));
  const r = normalizePath(root).replace(/\/$/, '') + '/';
  if (t === normalizePath(root).replace(/\/$/, '')) return true;
  return t.startsWith(r) && !t.includes('/../') && !t.includes('/..\\');
}

/**
 * Evaluate guardrail for a single tool call.
 * @param {string} toolName
 * @param {object} args
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {string}  opts.root — resolved project root
 * @returns {{ denied: boolean, reason?: string }}
 */
export function evaluateGuardrail(toolName, args, { enabled = false, root = '' } = {}) {
  if (!enabled) return { denied: false };
  const name = String(toolName ?? '').toLowerCase();

  if (name === 'run_command' && args && typeof args.command === 'string') {
    if (DELETE_PATTERNS.test(args.command)) {
      return { denied: true, reason: `auto-delete blocked by guardrail: "${args.command}"` };
    }
  }

  if (name === 'write_file' || name === 'apply_edit') {
    // Resolve the path-arg via the unified registry (E3-WS3-T04) — the same alias set the
    // executor reads (path / filePath / file / filename) so an external write can't slip
    // through an alias the guardrail forgot.
    const p = resolvePathArg(name, args);
    if (typeof p === 'string' && root && !isInsideRoot(root, p)) {
      return { denied: true, reason: `external write blocked by guardrail: "${p}" is outside root "${root}"` };
    }
  }

  return { denied: false };
}
